/**
 * Репозиторий контента: паки, пазлы, daily pool.
 * Источник правды — ContentLoader.loadContent() (content.json). Моковых данных нет.
 * Если контент не загружен, getContent() выбросит: "Content not loaded. Call ContentLoader.loadContent() first."
 */

import type { PackMeta, PuzzleMeta, PuzzleId, PackId } from '../data/types';
import { getContent } from './ContentLoader';

/** ID и путь к фиксированному пазлу туториала (всегда study.png). */
export const TUTORIAL_PUZZLE_ID: PuzzleId = 'tutorial';
export const TUTORIAL_PUZZLE_IMAGE_PATH = 'assets/img/study.png';

function buildPacksAndPuzzles(): { packs: PackMeta[]; puzzlesById: Map<PuzzleId, PuzzleMeta> } {
  const content = getContent();
  const packs: PackMeta[] = [];
  const puzzlesById = new Map<PuzzleId, PuzzleMeta>();

  puzzlesById.set(TUTORIAL_PUZZLE_ID, {
    id: TUTORIAL_PUZZLE_ID,
    source: 'daily',
    imagePath: TUTORIAL_PUZZLE_IMAGE_PATH,
    imageKey: TUTORIAL_PUZZLE_ID,
    title: 'Обучение',
  });

  content.dailyPool.forEach((p) => {
    puzzlesById.set(p.id, {
      id: p.id,
      source: 'daily',
      imagePath: p.full,
      imageKey: p.imageKey,
      title: p.title,
    });
  });

  content.packs.forEach((pack) => {
    const puzzleIds: PuzzleId[] = [];
    pack.puzzles.forEach((p, idx) => {
      const order = idx + 1;
      puzzleIds.push(p.id);
      puzzlesById.set(p.id, {
        id: p.id,
        source: 'pack',
        imagePath: p.full,
        imageKey: p.imageKey,
        title: p.title,
        packId: pack.id,
        orderInPack: order,
      });
    });
    packs.push({ id: pack.id, title: pack.title, puzzleIds });
  });

  return { packs, puzzlesById };
}

let cached: { packs: PackMeta[]; puzzlesById: Map<PuzzleId, PuzzleMeta> } | null = null;

function getCache(): { packs: PackMeta[]; puzzlesById: Map<PuzzleId, PuzzleMeta> } {
  if (cached === null) {
    cached = buildPacksAndPuzzles();
  }
  return cached;
}

/** Простой детерминированный хэш строки для выбора daily. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getAllPacks(): PackMeta[] {
  return [...getCache().packs];
}

export function getPuzzlesForPack(packId: PackId): PuzzleMeta[] {
  const { packs, puzzlesById } = getCache();
  const pack = packs.find((p) => p.id === packId);
  if (!pack) return [];
  return pack.puzzleIds
    .map((id) => puzzlesById.get(id))
    .filter((p): p is PuzzleMeta => p !== undefined);
}

export function getPuzzleMeta(puzzleId: PuzzleId): PuzzleMeta | undefined {
  return getCache().puzzlesById.get(puzzleId);
}

/** Найти пазл по id, imageKey или imagePath (для коллекции). */
export function getPuzzleMetaByKey(key: string): PuzzleMeta | undefined {
  const { puzzlesById } = getCache();
  const byId = puzzlesById.get(key);
  if (byId) return byId;
  for (const meta of puzzlesById.values()) {
    if (meta.imageKey === key) return meta;
    if (meta.imagePath === key) return meta;
  }
  return undefined;
}

/**
 * Детерминированно выбирает пазл из daily pool по ключу даты (YYYY-MM-DD).
 */
export function pickDailyPuzzle(dailyKey: string): PuzzleMeta {
  const content = getContent();
  const pool = content.dailyPool;
  const index = hashString(dailyKey) % pool.length;
  const p = pool[index];
  const meta = getCache().puzzlesById.get(p.id);
  if (!meta) throw new Error(`Daily puzzle ${p.id} not in cache`);
  return { ...meta, source: 'daily' };
}
